import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  Slider,
  Alert,
  LinearProgress,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import MicIcon from "@mui/icons-material/Mic";

const CustomVoiceModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    gender: "Female",
    accent: "American",
    ageRange: "Adult",
    style: "Professional",
    provider: "ElevenLabs",
    stability: 0.5,
    similarityBoost: 0.75,
    pitch: 1.0,
    speed: 1.0,
    sampleFile: null,
    sampleFileName: "",
  });

  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        gender: data.gender || "Female",
        accent: data.accent || "American",
        ageRange: data.ageRange || "Adult",
        style: data.style || "Professional",
        provider: data.provider || "ElevenLabs",
        stability: data.stability || 0.5,
        similarityBoost: data.similarityBoost || 0.75,
        pitch: data.pitch || 1.0,
        speed: data.speed || 1.0,
        sampleFile: null,
        sampleFileName: data.sampleFileName || "",
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      setFormData({
        ...formData,
        sampleFile: file,
        sampleFileName: file.name,
      });
    }
  };

  const handlePreview = () => {
    alert("Playing voice preview with current settings...");
  };

  const handleSubmit = () => {
    onSave(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      gender: "Female",
      accent: "American",
      ageRange: "Adult",
      style: "Professional",
      provider: "ElevenLabs",
      stability: 0.5,
      similarityBoost: 0.75,
      pitch: 1.0,
      speed: 1.0,
      sampleFile: null,
      sampleFileName: "",
    });
    setUploadProgress(0);
    setIsUploading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" fontWeight={600}>
            {isEdit ? "Edit Custom Voice" : "Add Custom Voice"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Alert */}
          <Alert severity="info">
            Upload audio samples (at least 1 minute) to clone a voice. Higher quality samples produce better results.
          </Alert>

          {/* Basic Information */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Voice Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label="Voice Name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                placeholder="e.g., Sarah - Customer Service"
              />
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={2}
                placeholder="Brief description of the voice characteristics"
              />
            </Box>
          </Box>

          <Divider />

          {/* Voice Characteristics */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Voice Characteristics
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={formData.gender}
                    label="Gender"
                    onChange={(e) => handleChange("gender", e.target.value)}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Non-binary">Non-binary</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Age Range</InputLabel>
                  <Select
                    value={formData.ageRange}
                    label="Age Range"
                    onChange={(e) => handleChange("ageRange", e.target.value)}
                  >
                    <MenuItem value="Young">Young (18-30)</MenuItem>
                    <MenuItem value="Adult">Adult (30-50)</MenuItem>
                    <MenuItem value="Middle Aged">Middle Aged (50-65)</MenuItem>
                    <MenuItem value="Senior">Senior (65+)</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Accent</InputLabel>
                  <Select
                    value={formData.accent}
                    label="Accent"
                    onChange={(e) => handleChange("accent", e.target.value)}
                  >
                    <MenuItem value="American">American</MenuItem>
                    <MenuItem value="British">British</MenuItem>
                    <MenuItem value="Australian">Australian</MenuItem>
                    <MenuItem value="Indian">Indian</MenuItem>
                    <MenuItem value="Canadian">Canadian</MenuItem>
                    <MenuItem value="Irish">Irish</MenuItem>
                    <MenuItem value="Scottish">Scottish</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Style</InputLabel>
                  <Select
                    value={formData.style}
                    label="Style"
                    onChange={(e) => handleChange("style", e.target.value)}
                  >
                    <MenuItem value="Professional">Professional</MenuItem>
                    <MenuItem value="Friendly">Friendly</MenuItem>
                    <MenuItem value="Warm">Warm</MenuItem>
                    <MenuItem value="Authoritative">Authoritative</MenuItem>
                    <MenuItem value="Energetic">Energetic</MenuItem>
                    <MenuItem value="Calm">Calm</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Provider Selection */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Voice Provider
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={formData.provider}
                label="Provider"
                onChange={(e) => handleChange("provider", e.target.value)}
              >
                <MenuItem value="ElevenLabs">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      ElevenLabs
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      High-quality voice cloning with natural intonation
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="PlayHT">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Play.ht
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ultra-realistic AI voice cloning
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="Resemble">
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Resemble AI
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-time voice cloning and synthesis
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Audio Sample Upload */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Audio Sample Upload
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Upload clear audio samples for voice cloning. Recommended: 1-5 minutes of clean speech.
            </Typography>
            <Card sx={{ mt: 2, border: "2px dashed #E0E0E0", backgroundColor: "#F9FAFB" }}>
              <CardContent>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <MicIcon sx={{ fontSize: 48, color: "primary.main" }} />
                  <input
                    accept="audio/*"
                    style={{ display: "none" }}
                    id="voice-sample-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="voice-sample-upload">
                    <CustomButton
                      icon={<CloudUploadIcon />}
                      iconPosition="left"
                      text="Upload Audio Sample"
                      component="span"
                    />
                  </label>
                  {formData.sampleFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Uploaded: {formData.sampleFileName}
                    </Typography>
                  )}
                  {isUploading && (
                    <Box sx={{ width: "100%", mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Uploading... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Divider />

          {/* Voice Parameters */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Voice Parameters
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Stability: {formData.stability.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Higher stability = more consistent voice, Lower stability = more expressive
                </Typography>
                <Slider
                  value={formData.stability}
                  onChange={(e, value) => handleChange("stability", value)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Similarity Boost: {formData.similarityBoost.toFixed(2)}
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  How closely the AI should match the original voice characteristics
                </Typography>
                <Slider
                  value={formData.similarityBoost}
                  onChange={(e, value) => handleChange("similarityBoost", value)}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Speaking Speed: {formData.speed.toFixed(2)}x
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Adjust how fast the voice speaks
                </Typography>
                <Slider
                  value={formData.speed}
                  onChange={(e, value) => handleChange("speed", value)}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0.5, label: "0.5x" },
                    { value: 1.0, label: "1x" },
                    { value: 2.0, label: "2x" },
                  ]}
                />
              </Box>

              <Box>
                <Typography variant="body2" gutterBottom fontWeight={600}>
                  Pitch: {formData.pitch.toFixed(2)}x
                </Typography>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Adjust voice pitch
                </Typography>
                <Slider
                  value={formData.pitch}
                  onChange={(e, value) => handleChange("pitch", value)}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  valueLabelDisplay="auto"
                  marks={[
                    { value: 0.5, label: "Low" },
                    { value: 1.0, label: "Normal" },
                    { value: 2.0, label: "High" },
                  ]}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <CustomButton
          icon={<PlayArrowIcon />}
          iconPosition="left"
          text="Preview Voice"
          variant="outlined"
          onClick={handlePreview}
        />
        <Box sx={{ flex: 1 }} />
        <CustomButton text="Cancel" variant="outlined" onClick={handleClose} />
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text={isEdit ? "Update Voice" : "Add Voice"}
          onClick={handleSubmit}
          disabled={!formData.name.trim() || !formData.sampleFileName}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CustomVoiceModal;
